import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useInfiniteQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Badge from "src/components/Badge"
import Button from "src/components/Button"

import { getJobTemplates } from "src/api/Service"

import useDebounce from "src/hooks/use_debounce"
import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import { computeSubtotal } from "../shared/helpers"
import {
  ADD_JOB_FROM_SCRATCH_CLICKED,
  ADD_JOB_TEMPLATE_SELECTED,
} from "./amplitude_events"

const JobTemplatePicker = ({
  onSelect,
  onStartFromScratch,
  boatLengthFeet,
}) => {
  const { marinaSlug, presentmentCurrency } = useMarina()
  const tracker = useTracker()
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSetSearchQuery] = useDebounce(setSearchQuery)
  const inputRef = useRef(null)
  const sentinelRef = useRef(null)

  const {
    data,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ["jobTemplates", marinaSlug, searchQuery, "active"],
    ({ pageParam = 1 }) =>
      getJobTemplates({
        search: searchQuery,
        page: pageParam,
        status: "active",
      }),
    {
      getNextPageParam: (lastPage) => {
        const { currentPage, totalPages } = lastPage.pagination ?? {}
        return currentPage != null && currentPage < totalPages
          ? currentPage + 1
          : undefined
      },
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  const templates = useMemo(
    () => data?.pages.flatMap((p) => p.data ?? []) ?? [],
    [data]
  )

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return undefined
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) handleLoadMore()
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [handleLoadMore, templates.length])

  const isInitialLoad = isFetching && templates.length === 0
  const hasNoTemplatesEver =
    searchQuery === "" && !isFetching && !isError && templates.length === 0
  const hasNoSearchResults =
    searchQuery !== "" && !isFetching && !isError && templates.length === 0

  const estimatedTemplateCost = (template) =>
    template.lineItems.reduce((sum, li) => {
      const isByLength = li.product?.pricingStructure === "by_length"
      const quantity = isByLength ? boatLengthFeet ?? 0 : li.quantity
      return (
        sum +
        computeSubtotal({
          pricePerUnit: li.product?.pricePerUnit,
          quantity,
          pricePrecision: li.product?.pricePrecision,
        })
      )
    }, 0)

  return (
    <div className="flex flex-col gap-4">
      {!hasNoTemplatesEver && (
        <input
          ref={inputRef}
          type="text"
          aria-label="Search job templates"
          placeholder="Search job templates..."
          className="h-10 w-full rounded border px-3 py-2 outline-none focus:border-blue-600"
          onChange={(e) => debouncedSetSearchQuery(e.target.value)}
        />
      )}

      {isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
          We couldn&rsquo;t load job templates. Try again in a moment, or start
          from scratch below.
        </div>
      ) : hasNoTemplatesEver ? (
        <div className="rounded-lg border bg-gray-100 p-6 text-center text-sm text-gray-600">
          No job templates yet.{" "}
          <a
            className="font-semibold text-blue-600 hover:underline"
            href={`/manage/${marinaSlug}/service/job_templates`}
          >
            Create one in Settings →
          </a>{" "}
          or start from scratch below.
        </div>
      ) : (
        <div className="max-h-80 overflow-auto rounded-lg border">
          {isInitialLoad ? (
            <div className="flex h-32 items-center justify-center text-sm text-gray-500">
              <span className="fa-solid fa-spinner-third fa-spin mr-2" />
              Loading templates…
            </div>
          ) : hasNoSearchResults ? (
            <div className="flex h-32 items-center justify-center text-sm text-gray-500">
              No templates match your search.
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => {
                      const liveQuery = inputRef.current?.value ?? searchQuery
                      tracker.trackEvent(ADD_JOB_TEMPLATE_SELECTED, {
                        templateId: template.id,
                        templateName: template.name,
                        query: liveQuery,
                        queryLength: liveQuery.length,
                        resultPosition: templates.indexOf(template),
                        resultCount: templates.length,
                      })
                      onSelect(template)
                    }}
                    className="flex w-full items-center justify-between bg-white px-4 py-3 text-left hover:bg-gray-100"
                  >
                    <div className="min-w-0 flex-1 pr-3">
                      <div className="flex items-center gap-2 font-semibold text-gray-900">
                        {template.name}
                        {template.hasInactiveLineItems && (
                          <span aria-label="Contains inactive items">
                            <Badge
                              color="neutral"
                              text="Inactive items"
                              variant="small"
                            />
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {template.description}
                      </div>
                      <div className="text-xs text-gray-500">
                        {template.lineItems.length} items ·{" "}
                        {formatCurrencyFromMinorUnits({
                          amount: estimatedTemplateCost(template),
                          currency: presentmentCurrency,
                        })}
                      </div>
                    </div>
                    <i
                      className="fa-solid fa-chevron-right text-xs text-gray-500"
                      aria-hidden
                    />
                  </button>
                ))}
              </div>
              <div ref={sentinelRef} className="h-px" />
              {isFetchingNextPage && (
                <div className="border-t p-3 text-center text-sm text-gray-500">
                  Loading…
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 text-sm text-gray-500">
        <div className="flex-1 border-t" />
        <span>or</span>
        <div className="flex-1 border-t" />
      </div>
      <div className="flex w-full justify-center">
        <Button
          variant="link"
          onClick={() => {
            tracker.trackEvent(ADD_JOB_FROM_SCRATCH_CLICKED, {})
            onStartFromScratch()
          }}
        >
          + Create new job from scratch
        </Button>
      </div>
    </div>
  )
}

JobTemplatePicker.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onStartFromScratch: PropTypes.func.isRequired,
  boatLengthFeet: PropTypes.number,
}

export default JobTemplatePicker
